import type { EndpointBuilder } from '@reduxjs/toolkit/query';

import { customCreateApi } from '../../context';
import { createDynamicBaseQuery } from '../../utils/create-dynamic-base-query';
import type { DynamicBaseQueryFn } from '../../utils/create-dynamic-base-query';
import { isAppContextState } from '../app-context-slice';
import { selectAuraConfiguration } from '../configuration/configuration-slice';
import { getAccessToken } from '../session-slice';
import {
  type CreateSettingsApiArg,
  type GetSettingsApiArg,
  LATEST_VERSION,
  type SettingsDocument,
  type UpdateSettingsApiArg,
} from './settings.api.types';

export const tagTypes = ['Settings'] as const;
export const LIST_ID = 'LIST';

export type TagType = (typeof tagTypes)[number];

export type Builder = EndpointBuilder<DynamicBaseQueryFn, TagType, 'settingsApi'>;

const BASE_PATH = '/v1/framework/settings';

const makeTag = <T extends TagType>(type: T, ...identifiers: (string | number)[]) => {
  return identifiers.length > 0 ? { type, id: identifiers.join(':') } : type;
};

const settingsApi = customCreateApi({
  baseQuery: createDynamicBaseQuery((state) => selectAuraConfiguration(state)?.sharedStorageApiUrl, {
    prepareHeaders: async (headers, { getState }) => {
      const accessToken = await getAccessToken();
      headers.set('Authorization', `Bearer ${accessToken}`);
      const state = getState();

      if (isAppContextState(state)) {
        const projectId = state.appContext.activeProjectId;
        if (projectId !== null) {
          headers.set('Project-Id', projectId);
        }
      }
    },
  }),
  reducerPath: 'settingsApi',
  tagTypes,
  endpoints: (builder) => ({
    createSettings: builder.mutation<SettingsDocument, CreateSettingsApiArg>({
      query: (arg) => ({
        url: BASE_PATH,
        method: 'POST',
        body: { ...arg.settings, version: LATEST_VERSION },
      }),
    }),
    getSettings: builder.query<Omit<SettingsDocument, 'createdAt' | 'updatedAt'> | undefined, GetSettingsApiArg>({
      query: () => ({
        url: `${BASE_PATH}?limit=1&sort=createdAt:asc`,
        method: 'GET',
      }),
      transformResponse: (response: { items: SettingsDocument[] }, _api) => {
        const [item] = response.items;
        return item;
      },
      providesTags: (_, __, { projectId }) => [makeTag('Settings', projectId, LIST_ID)],
    }),
    updateSetting: builder.mutation<SettingsDocument, UpdateSettingsApiArg>({
      query: ({ id, scope, key, value }) => ({
        url: `${BASE_PATH}/${id}`,
        method: 'PATCH',
        body: { [scope]: { [key]: value }, version: LATEST_VERSION },
      }),
      onQueryStarted: async ({ projectId, scope, key, value }, { dispatch, queryFulfilled }) => {
        const patchGetSettings = dispatch(
          settingsApi.util.updateQueryData('getSettings', { projectId }, (draft) => {
            if (draft !== undefined) {
              const draftScope: Record<string, unknown> = draft[scope];
              draftScope[key] = value;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchGetSettings.undo();
        }
      },
      invalidatesTags: (_, __, { projectId }) => [makeTag('Settings', projectId, LIST_ID)],
    }),
  }),
});

export default settingsApi;

export const { useGetSettingsQuery: useGetSettingsQueryInternal } = settingsApi;
